// Simplified Chinese (简体中文) dictionary. Implements MktDict — same shape as en.ts.
// Numbers (47/10/7/v4/60+/9) and proper nouns / brand names stay as-is.

import type { MktDict } from "./en";

export const zh: MktDict = {
  nav: {
    items: {
      pricing: "价格",
      examples: "示例",
      templates: "库",
      design: "设计",
      docs: "文档",
      developers: "开发者",
      blog: "博客",
      about: "关于",
    },
    openStudio: "打开 Studio →",
    themeToggle: "切换主题",
    language: "语言",
    status: "状态",
    signIn: "登录",
    menu: "菜单",
  },

  footer: {
    tagline: "把意图和源材料变成活的应用和文档。",
    groups: {
      product: {
        title: "产品",
        links: {
          pricing: "价格",
          templates: "库",
          docs: "文档",
          designSystem: "设计系统",
        },
      },
      company: {
        title: "公司",
        links: { about: "关于", blog: "博客", contact: "联系" },
      },
      compare: {
        title: "对比",
        links: { framer: "vs Framer", wix: "vs Wix", v0: "vs V0" },
      },
      legal: {
        title: "法律",
        links: { privacy: "隐私", terms: "条款", security: "安全" },
      },
    },
    copyright: "coogo · PADO Software Design L.L.C.",
    location: "迪拜, 阿联酋",
  },

  homeFooter: {
    groups: {
      product: {
        title: "产品",
        links: {
          templates: "库",
          designSystem: "设计系统",
          pricing: "价格",
          studio: "Studio",
          howItWorks: "工作原理",
        },
      },
      resources: {
        title: "资源",
        links: {
          documentation: "文档",
          blog: "博客",
          apiReference: "API 参考",
          templates: "库",
        },
      },
      company: {
        title: "公司",
        links: { aboutUs: "关于我们", contact: "联系", careers: "招聘" },
      },
      legal: {
        title: "法律",
        links: { terms: "条款", privacy: "隐私", refund: "退款" },
      },
    },
  },

  hero: {
    badge: "coogo Studio",
    h1: { line1: "活的应用。", line2: "活的文档", accent: "。" },
    subhead:
      "描述你想创建或修改的内容。coogo 会结合源材料工作，提出可见的修改，并让应用、文档或报告保持可审阅、可共享、可导出。",
    promptLines: [
      "一个带源材料、审阅备注和导出状态的活动报告…",
      "一个诊所预约应用 — 客户预约，员工收到短信提醒…",
      "一个带月度订阅和深色模式的会员专属播客…",
      "一个带推荐排行榜的猫咪寄养服务候补名单…",
    ],
    inputPlaceholder: "让 coogo 创建入门应用、转换资料或修改报告…",
    generate: "询问 coogo",
    chips: ["App", "Document", "Report", "审阅循环"],
  },

  why: {
    label: "§ 02 · why coogo",
    h2: "面向用户意图和 coogo agent 的工作空间。",
    intro:
      "coogo 不是静态页面生成器。它把意图和源材料变成可审阅、可修改、可信任的产出。",
    card1: {
      chip: "cross-platform",
      h3: "应用会持续保持可运行。",
      body: "请求产品界面、内部工具或移动工作流。coogo 会在 Studio 中打开运行中的应用，让后续请求落在同一个可见产出上。",
      annLeft: "web · cogo.xyz",
      annMid: "← same JSON →",
      annRight: "mobile · iOS · Android",
    },
    card2: {
      chip: "guarded generation",
      h3: "文档会保持可编辑。",
      body: "上传文件、请求结构，然后通过提案和批准来修改，而不是静默覆盖。文档会作为工作中的产出保留下来。",
    },
    card3: {
      chip: "design system",
      h3: "报告会连接源材料。",
      body: "图表、表格、文本和导出会连接源覆盖和验证检查，分享前能看到信任状态。",
      link: "查看设计系统 →",
    },
  },

  how: {
    label: "§ 03 · how it works",
    h2: "请求、审阅、批准、导出。",
    intro:
      "默认循环很简单：询问 coogo，检查产出，决定是否采用修改，然后发布、分享或导出。",
    steps: [
      {
        n: "01",
        title: "询问 coogo",
        body: "描述目标或附上源材料。请求会随 Studio handoff 保留，而不是塞进 URL。",
        caption: "意图已保存",
      },
      {
        n: "02",
        title: "查看产出",
        body: "应用、文档或报告成为中心界面。源材料和检查仍可查看，但不会取代工作本身。",
        caption: "产出优先",
      },
      {
        n: "03",
        title: "决定",
        body: "coogo 用清楚语言提出修改。产出改变前，你可以使用、要求修改或保持当前版本。",
        caption: "使用 / 修改 / 保持",
      },
      {
        n: "04",
        title: "分享或导出",
        body: "发布应用、分享审阅链接，或将文档和报告连同目标准备状态一起导出。",
        caption: "发布 / 分享 / 导出",
      },
    ],
  },

  templates: {
    label: "§ 04 · live work",
    h2: "从你已有的工作类型开始。",
    intro:
      "coogo home 使用与 Studio 相同的创作语言：application、document、report、revision。",
    all: "打开库",
    live: "就绪",
    preview: "开始",
    webMobile: "coogo Studio",
    items: [
      { name: "创建应用", cat: "生成应用工作区" },
      { name: "转换文档", cat: "DOCX, HWPX, PPTX, XLSX" },
      { name: "创建报告", cat: "源连接表格和图表" },
      { name: "修改现有工作", cat: "提案和批准循环" },
    ],
  },

  builtOn: {
    label: "§ 05 · built on",
    h2: "人与 agent 在同一个产出上工作。",
    intro:
      "以线条为主的界面展示用户、源材料、coogo、产出和审阅状态之间的连接。内部机制默认不占据主界面。",
    taglines: ["请求", "理解", "源材料", "产出", "验证"],
  },

  faq: {
    label: "§ 06 · questions",
    h2: "访客首先需要理解的事。",
    items: [
      {
        q: "coogo 是做应用还是文档？",
        a: "两者都是。产品承诺是活的应用和活的文档：coogo 在同一个 Studio 循环中创建、修改、审阅、分享和导出两类工作。",
      },
      {
        q: "我在首页输入后会发生什么？",
        a: "coogo 会把请求保存到 Studio handoff，打开工作空间，并让对话连接到产出，而不是把提示词放进 URL。",
      },
      {
        q: "agent 会静默修改我已接受的工作吗？",
        a: "不会。创作者界面的契约是先提案。coogo 会解释修改、显示目标，并在需要时等待使用、修改或保持当前版本。",
      },
      {
        q: "共享产出发布后如何管理？",
        a: "源材料包、审阅记录和导出状态会保存在一起，方便共享后继续改进。",
      },
      {
        q: "可以导出原生文件吗？",
        a: "可以。文档和报告可以面向 DOCX、HWPX、PPTX、XLSX、PDF 等格式，并按目标显示准备状态。",
      },
      {
        q: "首屏应该传达什么？",
        a: "coogo 可以通过对话和可见产出创建或修改应用、文档、报告。其他内容都服务于这个故事。",
      },
    ],
  },

  cta: {
    label: "§ 07 · ship",
    h2: { pre: "把想继续推进的工作", accent: "交给 coogo。" },
    body: "从请求、源文件或粗略报告想法开始。Studio 会把它推进到产出和下一次决策。",
    start: "打开 Studio",
    browse: "浏览库",
    chips: ["application", "document", "report", "approval loop"],
  },

  pricing: {
    label: "§ PRICING",
    h1: { line1: "一个价格，", line2: "活的产出。" },
    subhead: "应用、文档和报告都会包含审阅、共享和导出流程。",
    billing: { monthly: "按月", annual: "按年 — 省 20%" },
    perMonth: "/月",
    customPrice: "定制",
    contactForPricing: "价格请联系我们",
    billedAnnually: "按年计费",
    billedMonthly: "按月计费",
    tiers: [
      {
        name: "Hobby",
        tag: "永久免费",
        cta: "免费开始",
        bullets: ["1 个共享产出", "cogo.xyz 子域名", "社区支持", "100 MB 存储"],
      },
      {
        name: "Creator",
        tag: "最受欢迎",
        cta: "14 天免费试用",
        bullets: [
          "10 个共享产出",
          "自定义域名",
          "审阅工作流",
          "5 GB 存储",
          "邮件支持",
        ],
      },
      {
        name: "Studio",
        tag: "团队适用",
        cta: "开始试用",
        bullets: [
          "50 个共享产出",
          "团队工作区 (5 席)",
          "优先支持",
          "25 GB 存储",
          "高级分析",
        ],
      },
      {
        name: "Enterprise",
        tag: "定制",
        cta: "联系我们",
        bullets: ["无限产出", "SSO & SAML", "SLA", "专属成功经理", "定制合同"],
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "在你决定之前，几个问题",
    faq: [
      {
        q: "应用、文档和报告是否按同一种产出计算？",
        a: "是的。每个共享产出都会把源材料、审阅状态和导出路径保存在一起。",
      },
      {
        q: "我能用自己的域名吗？",
        a: "Creator 及以上可以。把域名指向 coogo，我们会自动处理 SSL。",
      },
      {
        q: "如果我降级会怎样？",
        a: "超出新层级的应用会转为只读，而不会被删除。随时升级即可重新激活。",
      },
      {
        q: "共享产出会按次额外收费吗？",
        a: "不会。套餐基于共享产出、AI 点数、存储和团队能力，而不是从你的工作成果中抽成。",
      },
    ],
  },

  about: {
    label: "§ ABOUT",
    h1: { line1: "coogo 创建", line2: "可以继续修改的工作。" },
    intro:
      "coogo 基于一个产品信念：AI 产出不应停在草稿。产出应该保持活着、可见、可审阅，并准备好发布或导出。",
    timeline: [
      { year: "2023", label: "起步", desc: "两名工程师，一家咖啡馆。" },
      { year: "2024", label: "公开测试", desc: "8 周内 1.2 万人注册。" },
      {
        year: "2025",
        label: "现在",
        desc: "数千个应用已发布；首尔 + 旧金山共 40 人。",
      },
    ],
    teamLabel: "§ TEAM",
    teamTitle: "机器背后的人",
    team: [
      { name: "Chan", role: "联合创始人 · CEO" },
      { name: "Min", role: "联合创始人 · CTO" },
      { name: "Ha-eun", role: "设计负责人" },
      { name: "Paul", role: "增长负责人" },
    ],
    contactLabel: "§ CONTACT",
    contactTitle: "打个招呼。",
    contactIntro: "媒体、合作，或只是想聊聊 — 每封邮件我们都会回复。",
    contactGeneral: "一般",
    contactPress: "媒体",
    contactEnterprise: "企业",
    formName: "姓名",
    formNamePlaceholder: "你的名字",
    formEmail: "邮箱",
    formEmailPlaceholder: "you@email.com",
    formMessage: "留言",
    formMessagePlaceholder: "告诉我们你在构建什么…",
    formSend: "发送留言",
  },

  templatesPage: {
    label: "§ GALLERY",
    h1: "从工作类型开始。",
    subhead:
      "选择一个应用、文档、报告或修订路径，然后让 coogo 围绕你的源材料继续推进。",
    cats: ["报告", "文档", "分析", "健康", "Starter", "SaaS", "管理", "效率"],
  },

  blog: {
    label: "§ JOURNAL",
    h1: "实地笔记。",
    subhead: "关于构建者、创造者和软件的大声思考。",
    featuredSuffix: "精选",
    featuredExcerpt:
      "十年来，我看着小企业为两个应用付费，最终只有一个能用。这是我写下的、解释我们为何开始打造一台不同的机器的文章。",
    minRead: "分钟",
    posts: [
      { t: "我们为何打造 coogo", tag: "Manifesto" },
      { t: "从第一天起就有原生移动端", tag: "Product" },
      { t: "塑造 coogo 的三个信念", tag: "Philosophy" },
      { t: "编辑一次，处处更新：绑定的工作原理", tag: "Engineering" },
      { t: "写给小应用的情书", tag: "Essay" },
      { t: "Starter 聚焦：源连接报告", tag: "Showcase" },
    ],
  },

  docs: {
    searchPlaceholder: "搜索文档",
    sections: [
      {
        h: "快速开始",
        items: ["介绍", "创建你的第一个产出", "分享或发布", "下一步去哪"],
      },
      {
        h: "构建",
        items: ["coogo JSON 概览", "模式参考", "区块", "事件与流程", "表达式"],
      },
      {
        h: "ACTIONFLOW",
        items: ["概览", "执行模式", "动作目录 (60+)", "Validator 循环"],
      },
      {
        h: "STUDIO",
        items: ["概览", "仪表盘", "共享", "IDE 工作区", "主题编辑器"],
      },
      { h: "设计系统", items: ["概览", "令牌", "源模型", "扩展系统"] },
      {
        h: "参考",
        items: [
          "设计系统页面",
          "组件 (进行中)",
          "源模型 (进行中)",
          "生成 (进行中)",
          "部署 (进行中)",
          "API 参考 (进行中)",
        ],
      },
    ],
    centerLabel: "快速开始",
    h1: "介绍",
    intro:
      "coogo 把用户意图和源材料变成可审阅、可修改、可共享、可导出的应用、文档和报告。",
    calloutTitle: "第一次用 coogo？",
    calloutBody: " 首先描述工作，而不是实现方式。Studio 会把对话连接到产出。",
    threeTitle: "需要知道的三件事",
    three: [
      {
        title: "产出优先。",
        body: " 中心界面应该是应用、文档或报告，而不是内部检查面板。",
      },
      {
        title: "coogo 先提案再应用。",
        body: " 修改应该是可理解的建议，用户可以使用、修改或保持当前版本。",
      },
      {
        title: "信任状态可见。",
        body: " 源覆盖、准备状态、导出阻塞和验证会在需要时显示。",
      },
    ],
    quickStartTitle: "快速开始",
    quickStart: {
      c1: "# 1. 捕获意图",
      c2: "# 2. 审阅可见产出",
      c3: "# 3. 分享、发布或导出",
    },
    next: "下一步",
    nextLink: "创建你的第一个产出 →",
    onThisPage: "本页内容",
    toc: ["介绍", "需要知道的三件事", "快速开始"],
  },

  designSystem: {
    sidebar: {
      groups: { foundations: "基础", system: "系统", contract: "契约" },
      items: {
        overview: "概览",
        philosophy: "理念",
        architecture: "架构",
        tokens: "令牌",
        components: "组件",
        patterns: "模式",
        rules: "Tailwind 规则",
        ontology: "源模型",
        schema: "JSON Schema",
        prompt: "LLM 提示",
        samples: "示例",
      },
      source: "源",
    },
    heroChip: "coogo 设计系统 · v4",
    heroH1: { line1: "封闭的词汇，", accent: "开放的表达。" },
    heroSubtitle: "营销、IDE、审阅、仪表盘、聊天 — 所有界面用同一套词汇。",
    sections: {
      philosophy: {
        name: "理念",
        h2: "封闭词汇，开放表达。",
        sub: "coogo DS 的第一原则。",
      },
      architecture: {
        name: "架构",
        h2: "一份源，六个产物。",
        sub: "在 YAML 中编辑。其余全部生成。",
      },
      tokens: {
        name: "令牌",
        h2: "47 个令牌，零漂移。",
        sub: "47 个令牌，零漂移。",
      },
      components: {
        name: "组件",
        h2: "一个按钮，三个世界。",
        sub: "一个按钮，三个世界。",
      },
      patterns: {
        name: "模式",
        h2: "10 个原型，无限产出。",
        sub: "10 个原型，无限产出。",
      },
      rules: {
        name: "Tailwind 规则",
        h2: "骨架用 className，外观用 variant。",
        sub: "骨架用 className，外观用 variant。",
      },
      ontology: {
        name: "源模型",
        h2: "源事实和检查信息附着在产出上。",
        sub: "开发者细节可按需打开，不占据创作者默认视图。",
      },
      schema: {
        name: "JSON Schema",
        h2: "五个文件声明一个区块。",
        sub: "五个文件声明一个区块。",
      },
      prompt: {
        name: "LLM 提示",
        h2: "简报 → 生成 → 校验 → 重试。",
        sub: "简报 → 生成 → 校验 → 重试。",
      },
      samples: {
        name: "示例",
        h2: "一套词汇，五个区块。",
        sub: "同一套词汇，五个区块。",
      },
    },
  },

  pricingPage: {
    h1: "价格",
    subhead: "为认真对待业务的创作者打造。",
    currencyNote: "所有方案均以阿联酋迪拉姆（AED）计费。",
    startFree: "免费开始",
    subscribeMonthly: "按月订阅",
    subscribeYearly: "按年订阅",
    perMonth: "/ 月",
    perYear: "/ 年",
    billingMonthly: "按月",
    billingYearly: "按年",
    yearlySaveHint: "免费 2 个月",
    yearlySaveLabel: "每年省 {amount}（立省 {pct}%）",
    reassureFree: "无需信用卡",
    reassurePaid: "随时取消 · 安全结账",
    errPayment: "支付失败",
    errNoUrl: "未收到支付链接",
    errNotAuth: "未认证",
    plans: {
      free: {
        title: "免费",
        description: "最适合：验证第一个产品想法的创作者。",
        features: [
          "1 个共享产出",
          "每月 500 AI 额度",
          "2% 交易手续费",
          "标准支持",
        ],
        badge: "",
      },
      creator: {
        title: "Creator",
        description: "最适合：共享多个产出的团队。",
        features: [
          "5 个共享产出",
          "每月 5,000 AI 额度",
          "审阅记录",
          "自定义域名支持",
          "基础分析",
        ],
        badge: "最受欢迎",
      },
      business: {
        title: "Business",
        description: "最适合：运营大量共享产出的组织。",
        features: [
          "30 个共享产出",
          "每月 20,000 AI 额度",
          "团队审阅记录",
          "高级分析",
          "优先支持",
        ],
        badge: "",
      },
    },
    faq: {
      title: "价格常见问题",
      items: [
        {
          q: "按什么货币计费？",
          a: "所有方案均以阿联酋迪拉姆（AED）计费。你看到的价格就是实付价格，没有隐藏的汇率费用。",
        },
        {
          q: "按月和按年有何区别？",
          a: "按年计费可享两个月免费——比按月支付约省 17%。可用上方的切换按钮在按月/按年之间切换。",
        },
        {
          q: "有免费试用吗？",
          a: "没有限时试用。免费方案无需信用卡即可一直使用，你可以先做出一个上线的应用，需要时再升级。",
        },
        {
          q: "可以随时取消吗？",
          a: "可以。在账户设置中随时取消——当前计费周期结束前方案仍有效，下个周期不再扣费。订阅不可退款，详见退款与取消政策。",
        },
        {
          q: "共享产出发布后如何管理？",
          a: "每个产出都会保留源材料包、审阅记录和导出状态，团队可以在共享后继续改进。",
        },
        {
          q: "可以使用自己的域名吗？",
          a: "可以。应用默认发布到免费的 name.cogo.xyz 地址，你可以在设置面板中连接自定义域名，并将 DNS 指向我们的边缘网络。",
        },
        {
          q: "AI 额度是什么？",
          a: "AI 额度是你每月用于 AI 工作（生成和编辑应用）的用量额度。各方案每月分别包含 500（免费）、5,000（Creator）、20,000（Business）额度。",
        },
      ],
    },
  },

  consent: {
    title: "支付协议",
    description: "在继续之前，请查看并同意我们的政策。",
    agreeIntro: "我同意",
    and1: "和",
    and2: "，并确认我已阅读",
    suffix: "。",
    termsLabel: "条款与条件",
    privacyLabel: "隐私政策",
    refundLabel: "退款与取消政策",
    cancel: "取消",
    processing: "处理中...",
    proceed: "前往支付",
    close: "关闭",
  },
};
